
import React, { useState, useCallback, useEffect, useRef } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { EnrollmentApi } from '@/features/enrollment/services/enrollment.service'
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft, ArrowRight, Shield, Eye, MessageSquare, RefreshCw, Download, Eraser } from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'

interface ComplianceStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleSimpleNavigate?: (targetIndex: number) => void
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    onComplete: () => void
    sessionId?: string
    [key: string]: any
}

export default function ComplianceStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleSimpleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    onComplete,
    sessionId,
    isMasterMode = false,
    revealAllFields = false
}: ComplianceStepProps) {
    const { user } = useAuth()
    const isFmoMasterView = !!(isMasterMode && revealAllFields && (user?.role === 'fmo_ceo' || user?.role === 'sales_manager' || user?.role === 'admin'))

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [showPayload, setShowPayload] = useState(false)
    const [smsConsentStatus, setSmsConsentStatus] = useState<any>(null)
    const [smsConsentError, setSmsConsentError] = useState<string | null>(null)
    const [isSendingSms, setIsSendingSms] = useState(false)
    const [isRefreshingSms, setIsRefreshingSms] = useState(false)
    const autoSentRef = useRef(false)

    // Signature 4 (Insurance Review) Drawing State
    const [drawingPadActive, setDrawingPadActive] = useState(false)
    const [hasSigned, setHasSigned] = useState(!!enrollmentData.drawableSignature4)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    // Redraw signature on load if it exists
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.strokeStyle = '#000000'
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        const signatureData = enrollmentData.drawableSignature4
        if (signatureData) {
            const img = new window.Image()
            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            }
            img.src = signatureData
        }
    }, [enrollmentData.drawableSignature4])

    const getCoordinates = (event: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current
        if (!canvas) return { x: 0, y: 0 }
        const rect = canvas.getBoundingClientRect()

        let clientX, clientY
        if ('touches' in event) {
            clientX = event.touches[0].clientX
            clientY = event.touches[0].clientY
        } else {
            clientX = (event as React.MouseEvent).clientX
            clientY = (event as React.MouseEvent).clientY
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        }
    }

    const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
        setDrawingPadActive(true)
        const { x, y } = getCoordinates(event)
        const ctx = canvasRef.current?.getContext('2d')
        if (ctx) {
            ctx.beginPath()
            ctx.moveTo(x, y)
        }
    }

    const draw = (event: React.MouseEvent | React.TouchEvent) => {
        if (!drawingPadActive) return
        event.preventDefault()
        const { x, y } = getCoordinates(event)
        const ctx = canvasRef.current?.getContext('2d')
        if (ctx) {
            ctx.lineTo(x, y)
            ctx.stroke()
            setHasSigned(true)
        }
    }

    const stopDrawing = () => {
        setDrawingPadActive(false)
        const canvas = canvasRef.current
        if (canvas) {
            const dataUrl = canvas.toDataURL()
            updateEnrollmentData('drawableSignature4', dataUrl)
            setHasSigned(true)
        }
    }

    const clearSignature = () => {
        const canvas = canvasRef.current
        const ctx = canvas?.getContext('2d')
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            updateEnrollmentData('drawableSignature4', null)
            setHasSigned(false)
        }
    }

    // Auto-fill compliance fields with default values on mount
    useEffect(() => {
        if (!isMasterMode) {
            // Question 1: Default to CMS Call completed
            if (!enrollmentData.authorizedUserConsent) {
                updateEnrollmentData('authorizedUserConsent', 'CMS Call Was Completed And Client Gave Authorization')
            }
            // Question 2: Default to Yes for permission
            if (!enrollmentData.permissionGiven) {
                updateEnrollmentData('permissionGiven', 'Yes')
            }
            // Question 3: Default to No other insurance
            if (!enrollmentData.hasInsurance) {
                updateEnrollmentData('hasInsurance', 'No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance')
            }
            // Question 4: Default to YES for terms
            if (!enrollmentData.termsAgreement) {
                updateEnrollmentData('termsAgreement', 'YES')
            }
        }
    }, [isMasterMode]) // Run once on mount or when mode changes

    const loadSmsConsentStatus = useCallback(async (showSpinner: boolean = false) => {
        if (!sessionId) return

        try {
            if (showSpinner) setIsRefreshingSms(true)
            const data = await EnrollmentApi.getSmsConsentStatus(sessionId)
            setSmsConsentStatus(data)
            setSmsConsentError(null)
        } catch (error) {
            setSmsConsentError(error instanceof Error ? error.message : 'Failed to load SMS consent status')
        } finally {
            if (showSpinner) setIsRefreshingSms(false)
        }
    }, [sessionId])

    useEffect(() => {
        loadSmsConsentStatus()
    }, [loadSmsConsentStatus])

    useEffect(() => {
        if (!smsConsentStatus?.smsConsent) return

        const status = smsConsentStatus.smsConsent.status
        if (status !== 'sent') return

        const timer = window.setInterval(() => {
            loadSmsConsentStatus()
        }, 5000)

        return () => window.clearInterval(timer)
    }, [smsConsentStatus, loadSmsConsentStatus])

    const handleSendSmsConsent = useCallback(async () => {
        if (!sessionId) {
            setSmsConsentError('Session ID is missing — please restart the enrollment')
            return
        }

        try {
            setIsSendingSms(true)
            setSmsConsentError(null)
            const data = await EnrollmentApi.sendSmsConsent(sessionId, enrollmentData)
            setSmsConsentStatus({
                sessionId,
                smsConsent: data,
                isReadyForSubmission: false
            })
        } catch (error) {
            setSmsConsentError(error instanceof Error ? error.message : 'Failed to send SMS consent')
        } finally {
            setIsSendingSms(false)
        }
    }, [sessionId, enrollmentData])

    useEffect(() => {
        const clientPhone = enrollmentData.clientPhone || ''
        if (!sessionId || !clientPhone || isSendingSms) return

        const existingPhone = smsConsentStatus?.smsConsent?.toPhone || ''
        const hasExistingConsent = !!smsConsentStatus?.smsConsent
        const needsInitialSend = !hasExistingConsent
        const phoneChanged = hasExistingConsent && existingPhone && !existingPhone.includes(clientPhone.replace(/\D/g, '').slice(-10))

        if ((needsInitialSend || phoneChanged) && !autoSentRef.current) {
            autoSentRef.current = true
            handleSendSmsConsent()
        }
    }, [sessionId, enrollmentData.clientPhone, smsConsentStatus, isSendingSms, handleSendSmsConsent])

    const handleDownloadSmsProof = useCallback(async () => {
        if (!sessionId) return

        try {
            const blob = await EnrollmentApi.downloadSmsConsentProof(sessionId)
            const url = window.URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = `sms-consent-proof-${sessionId}.pdf`
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            setSmsConsentError(error instanceof Error ? error.message : 'Failed to download SMS consent proof')
        }
    }, [sessionId])

    useEffect(() => {
        if (smsConsentStatus?.smsConsent?.proofFileUrl) {
            updateEnrollmentData('smsConsentProofUrl', smsConsentStatus.smsConsent.proofFileUrl)
        }
        if (smsConsentStatus?.smsConsent?.proofGeneratedAt) {
            updateEnrollmentData('smsConsentProofGeneratedAt', smsConsentStatus.smsConsent.proofGeneratedAt)
        }
    }, [smsConsentStatus, updateEnrollmentData])

    const handleSubmit = useCallback(async () => {
        const validationErrors: ValidationErrors = {}

        if (isMasterMode && revealAllFields) {
            if (!enrollmentData.authorizedUserConsent) {
                validationErrors.authorizedUserConsent = 'Please select CMS authorization status'
            }
            if (isFmoMasterView) {
                if (!enrollmentData.permissionGiven) {
                    validationErrors.permissionGiven = 'Please confirm permission'
                }
                if (!enrollmentData.hasInsurance) {
                    validationErrors.hasInsurance = 'Please select insurance enrollment status'
                }
            }
            if (!enrollmentData.termsAgreement) {
                validationErrors.termsAgreement = 'Please agree to terms and conditions'
            }
        }
        if (!(smsConsentStatus?.isReadyForSubmission && smsConsentStatus?.smsConsent?.lastReplyNormalized === 'YES')) {
            validationErrors.smsConsent = 'Client must reply YES to the SMS enrollment consent before submission'
        }
        if (!enrollmentData.drawableSignature4) {
            validationErrors.signature4 = 'Client signature for Insurance Review is required'
        }

        setErrors(validationErrors)

        if (Object.keys(validationErrors).length > 0) {
            console.warn('⚠️ ComplianceStep validation failed:', validationErrors)
            return
        }

        try {
            setIsSubmitting(true)
            setSubmitError(null)

            if (!sessionId) {
                throw new Error('Session ID is missing — please restart the enrollment')
            }

            // Build final submission data with current form values
            const submissionData = {
                ...enrollmentData,
                authorizedUserConsent: enrollmentData.authorizedUserConsent || 'CMS Call Was Completed And Client Gave Authorization',
                permissionGiven: enrollmentData.permissionGiven || 'Yes',
                hasInsurance: enrollmentData.hasInsurance || 'No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance',
                termsAgreement: enrollmentData.termsAgreement || 'YES',
            }

            console.log('🚀 Submitting to JotForm — sessionId:', sessionId)
            console.log('📋 Top-level keys:', Object.keys(submissionData).join(', '))

            await EnrollmentApi.submitACAForm(sessionId, submissionData)
            console.log('✅ JotForm submission successful')

            setIsSubmitted(true)

            // Auto-navigate to next step (Follow-Up)
            setTimeout(() => {
                if (handleNavigateWithCompletion && currentStepIndex !== undefined) {
                    handleNavigateWithCompletion(currentStepIndex + 1)
                } else {
                    handleNavigate(5)
                }
            }, 1500)

        } catch (error) {
            console.error('❌ JotForm submission failed:', error)
            setSubmitError(error instanceof Error ? error.message : 'Failed to submit. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }, [enrollmentData, sessionId, handleNavigate, handleNavigateWithCompletion, currentStepIndex, smsConsentStatus, isFmoMasterView])


    const handleBack = useCallback(() => {
        if (currentStepIndex !== undefined && currentStepIndex > 0) {
            if (handleSimpleNavigate) {
                handleSimpleNavigate(currentStepIndex - 1)
            } else {
                handleNavigate(currentStepIndex - 1)
            }
        }
    }, [currentStepIndex, handleSimpleNavigate, handleNavigate])

    const previewAgentName = `${enrollmentData.referralAgentFirstName || ''} ${enrollmentData.referralAgentLastName || ''}`.trim() || '[Agent Name]'
    const previewNpn = enrollmentData.referralAgentNpn || enrollmentData.agentNpn || enrollmentData.agentLicenseNumber || '[Your NPN]'
    const previewPlanName = enrollmentData.smsConsentPlanName || enrollmentData.selectedPlanName || enrollmentData.planName || enrollmentData.carrierDesiredPlanName || enrollmentData.carrierDesired || 'Ambetter Everyday Bronze 2026'
    const previewClientName = `${(enrollmentData.clientFirstName || '').trim()} ${(enrollmentData.clientLastName || '').trim()}`.trim() || '[Client Name]'
    const previewMessage = smsConsentStatus?.smsConsent?.outboundBody || `Hi ${previewClientName}, this is ${previewAgentName}, licensed health insurance agent (NPN #${previewNpn}), Health Connect Marketing.\nYou submitted a form to enroll in ${previewPlanName}. Please reply YES to confirm you want to enroll.\nReply NO if you do not wish to enroll.`

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 4}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            isNavigating={isNavigating}
            onComplete={onComplete}
            showNextButton={false}
            customButtons={
                <div className="flex justify-between">
                    <button
                        onClick={handleBack}
                        disabled={isNavigating || isSubmitting}
                        className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isNavigating || isSubmitting || isSubmitted}
                        className="btn-primary flex items-center space-x-2 min-w-[200px] justify-center"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Submitting...</span>
                            </>
                        ) : isSubmitted ? (
                            <>
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Proceeding to Follow-Up...</span>
                            </>
                        ) : (
                            <>
                                <span>Complete Enrollment</span>
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            }
        >
            <div className="card text-left">
                {/* Submission Status */}
                {isSubmitting && (
                    <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center space-x-3 animate-pulse">
                        <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                        <span className="text-indigo-700 font-medium">Submitting enrollment...</span>
                    </div>
                )}

                {submitError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-red-700 font-bold block mb-1">Submission Failed</span>
                            <span className="text-red-600 text-sm">{submitError}</span>
                            <p className="mt-2 text-xs text-red-500 italic">Click "Complete Enrollment" to retry.</p>
                        </div>
                    </div>
                )}

                {isSubmitted && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium">Enrollment submitted! Redirecting to Follow-Up...</span>
                    </div>
                )}

                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Compliance & Authorization</h3>
                        <p className="text-sm text-accent-600">Final CMS authorization and permissions.</p>
                    </div>
                </div>

                {/* Master Mode: Signature & Payload Audit */}
                {(isMasterMode || revealAllFields) && (
                    <div className="mb-8 p-5 bg-amber-50 border-2 border-amber-200 rounded-xl shadow-inner animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-amber-700" />
                                <h4 className="text-sm font-black text-amber-900 uppercase tracking-tighter">Signature & Payload Audit</h4>
                            </div>
                            <button
                                onClick={() => setShowPayload(true)}
                                className="flex items-center gap-1.5 px-3 py-1 bg-amber-200 hover:bg-amber-300 text-amber-900 text-[10px] font-bold rounded transition-colors shadow-sm"
                            >
                                <Eye className="w-3 h-3" />
                                VIEW RAW PAYLOAD
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <div className="bg-white border border-amber-100 rounded-lg p-3">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">Signature Metadata</p>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Actual Timestamp:</span>
                                            <span className="font-mono font-bold text-amber-900 text-[10px]">{new Date().toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Spoofed Offset (Doc 5):</span>
                                            <span className="font-mono font-bold text-indigo-600 text-[10px]">+1.4 Hours</span>
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-amber-700">Signature Method:</span>
                                            <span className="font-mono font-bold text-amber-900 text-[10px]">DRAW_WEB_CANVAS</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="p-3 bg-white rounded-lg border border-amber-100 text-[11px]">
                                    <p className="text-[10px] font-bold text-amber-600 uppercase mb-2">Document ID Mapping</p>
                                    <div className="space-y-1">
                                        <div className="flex justify-between">
                                            <span className="text-amber-700">Intake/Consent:</span>
                                            <span className="text-amber-900 font-mono text-[10px]">mapped_502</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-amber-700">Insurance Review:</span>
                                            <span className="text-amber-900 font-mono text-[10px]">mapped_911</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payload Modal */}
                        {showPayload && (
                            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden border border-amber-200">
                                    <div className="p-4 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Shield className="w-5 h-5 text-amber-700" />
                                            <h3 className="font-bold text-amber-900">Carrier Submission Payload Preview</h3>
                                        </div>
                                        <button
                                            onClick={() => setShowPayload(false)}
                                            className="p-1 hover:bg-amber-100 rounded-full transition-colors"
                                        >
                                            <ArrowLeft className="w-5 h-5 text-amber-700" />
                                        </button>
                                    </div>
                                    <div className="flex-1 overflow-auto p-6 bg-slate-900">
                                        <pre className="text-indigo-300 font-mono text-sm leading-relaxed">
                                            {JSON.stringify({
                                                sessionId,
                                                ...enrollmentData,
                                                systemMetadata: {
                                                    isMasterMode: true,
                                                    capturedAt: new Date().toISOString(),
                                                    agentOverride: true,
                                                    signatureCount: 5
                                                }
                                            }, null, 2)}
                                        </pre>
                                    </div>
                                    <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                                        <button
                                            onClick={() => setShowPayload(false)}
                                            className="px-6 py-2 bg-accent-900 text-white font-bold rounded-lg hover:bg-accent-800 transition-colors"
                                        >
                                            Close Audit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="space-y-6">
                    {/* SMS Consent Capture */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <h4 className="text-md font-semibold text-blue-900 flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5" />
                                    SMS Enrollment Consent
                                </h4>
                                <p className="text-sm text-blue-700 mt-1">
                                    Send the enrollment confirmation text, wait for the client to reply <strong>YES</strong>, then complete enrollment.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => loadSmsConsentStatus(true)}
                                    disabled={isRefreshingSms || isSendingSms}
                                    className="px-3 py-2 text-xs font-semibold rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-60 flex items-center gap-1.5"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${isRefreshingSms ? 'animate-spin' : ''}`} />
                                    Refresh
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSendSmsConsent}
                                    disabled={isSendingSms || isSubmitting}
                                    className="px-3 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 flex items-center gap-1.5"
                                >
                                    {isSendingSms ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                                    {isSendingSms ? 'Sending...' : 'Send Consent SMS'}
                                </button>
                                {smsConsentStatus?.smsConsent && (
                                    <button
                                        type="button"
                                        onClick={handleDownloadSmsProof}
                                        className="px-3 py-2 text-xs font-semibold rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-100 flex items-center gap-1.5"
                                    >
                                        <Download className="w-3.5 h-3.5" />
                                        Proof PDF
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                                <p className="text-xs font-bold uppercase text-blue-500 mb-2">Message Preview</p>
                                <p className="text-blue-900 whitespace-pre-line leading-relaxed">
                                    {previewMessage}
                                </p>
                            </div>
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                                <p className="text-xs font-bold uppercase text-blue-500 mb-2">Current Status</p>
                                {smsConsentStatus?.smsConsent ? (
                                    <div className="space-y-1.5 text-blue-900">
                                        <p><strong>Status:</strong> {smsConsentStatus.smsConsent.status || 'unknown'}</p>
                                        <p><strong>Phone:</strong> {smsConsentStatus.smsConsent.toPhone || enrollmentData.clientPhone || 'N/A'}</p>
                                        <p><strong>Sent At:</strong> {smsConsentStatus.smsConsent.sentAt ? new Date(smsConsentStatus.smsConsent.sentAt).toLocaleString() : 'Not sent yet'}</p>
                                        <p><strong>Reply:</strong> {smsConsentStatus.smsConsent.lastReplyBody || 'Awaiting client reply'}</p>
                                        <p><strong>Reply Captured:</strong> {smsConsentStatus.smsConsent.lastReplyAt ? new Date(smsConsentStatus.smsConsent.lastReplyAt).toLocaleString() : 'N/A'}</p>
                                        <p><strong>Proof Stored:</strong> {smsConsentStatus.smsConsent.proofFileUrl ? 'Yes' : 'Pending'}</p>
                                    </div>
                                ) : (
                                    <p className="text-blue-800">SMS consent will send automatically once the client phone is available.</p>
                                )}
                            </div>
                        </div>

                        {smsConsentStatus?.isReadyForSubmission ? (
                            <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Client replied YES. SMS consent requirement is satisfied.
                            </div>
                        ) : (
                            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                Submission remains blocked until the client replies YES.
                            </div>
                        )}

                        {(errors as any).smsConsent && (
                            <p className="mt-3 text-sm text-red-600">{(errors as any).smsConsent}</p>
                        )}
                        {smsConsentError && (
                            <p className="mt-3 text-sm text-red-600">{smsConsentError}</p>
                        )}
                    </div>

                    {/* Client Signature for Insurance Review */}
                    <div className="p-4 border border-gray-300 rounded-md bg-white">
                        <div className="mb-2 flex justify-between items-start gap-3 text-sm text-gray-500">
                            <div>
                                <div className="font-semibold text-gray-900">Client Signature (Insurance Review) *</div>
                                <div className="text-xs text-gray-500">Used for Insurance Review PDF. CLIENT MUST SIGN THIS. AGENT IS NOT ALLOWED TO SIGN.</div>
                            </div>
                            <button
                                type="button"
                                onClick={clearSignature}
                                className="flex items-center text-gray-600 hover:text-red-600 text-xs font-medium"
                            >
                                <Eraser className="w-4 h-4 mr-1" /> Clear
                            </button>
                        </div>
                        <div className="bg-white border border-gray-400 rounded cursor-crosshair h-40 w-full touch-none">
                            <canvas
                                ref={canvasRef}
                                className="w-full h-full"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                        </div>
                        <div className="mt-2 text-xs text-gray-500 flex justify-between items-center">
                            <span>
                                Status:{' '}
                                <span className={hasSigned ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                    {hasSigned ? 'Captured' : 'Missing'}
                                </span>
                            </span>
                        </div>
                        {errors.signature4 && <p className="mt-1 text-sm text-red-600 font-medium">{errors.signature4}</p>}
                    </div>

                    {/* CMS Call Compliance - MASTER MODE ONLY */}
                    {(isMasterMode && revealAllFields) && (
                        <div>
                            <h4 className="text-md font-semibold text-accent-800 mb-4">CMS Authorization</h4>
                        <div className="p-4 rounded-md border border-gray-200 text-sm text-gray-800 mb-4">
                            <p className="font-bold mb-2">COMPLIANCE: Call CMS Hotline 800-318-2596 With The Customer On The Phone and Say The Following:</p>
                            <div className="bg-gray-50 border-l-4 border-gray-400 pl-4 py-3 my-4">
                                <p className="font-medium text-base leading-relaxed text-gray-900 italic">
                                    "The customer gives approval to Health Connect Marketing and its licensed agents Anthony Ciponaer (NPN: 20867414) and Ashley Harmer (NPN: 22126690) to be the Authorized User & Authorized Agent to speak on the client's behalf for 365 Days. The client also wishes to revoke all other authorized users"
                                </p>
                            </div>
                            <p className="italic font-medium text-gray-600">Make sure the CMS customer support spells the Agent's name correctly.</p>
                        </div>

                        {/* Compliance Fields - Visible for agent to fill */}
                        <div className="space-y-6 mt-6">
                            {/* Authorized User Consent For 365 Days */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    Authorized User Consent For 365 Days <span className="text-red-500">*</span>
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                                        <input
                                            type="radio"
                                            name="authorizedUserConsent"
                                            value="CMS Call Was Completed And Client Gave Authorization"
                                            checked={enrollmentData.authorizedUserConsent === 'CMS Call Was Completed And Client Gave Authorization'}
                                            onChange={(e) => updateEnrollmentData('authorizedUserConsent', e.target.value)}
                                            className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                        />
                                        <span className="ml-3 text-sm font-medium text-gray-700">CMS Call Was Completed And Client Gave Authorization</span>
                                    </label>
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-yellow-50 transition-colors border-yellow-200 bg-yellow-50">
                                        <input
                                            type="radio"
                                            name="authorizedUserConsent"
                                            value="CMS CALL Will be completed later"
                                            checked={enrollmentData.authorizedUserConsent === 'CMS CALL Will be completed later'}
                                            onChange={(e) => updateEnrollmentData('authorizedUserConsent', e.target.value)}
                                            className="focus:ring-yellow-500 h-4 w-4 text-yellow-600 border-gray-300"
                                        />
                                        <span className="ml-3 text-sm font-medium text-yellow-800">CMS CALL Will be completed later</span>
                                    </label>
                                </div>
                                {errors.authorizedUserConsent && <p className="mt-2 text-sm text-red-600">{errors.authorizedUserConsent}</p>}
                            </div>

                            {/* Permission with Highlighted Agent Names */}
                            {isFmoMasterView && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                                        I give Health Connect Marketing and its Licensed Agents{' '}
                                        <span className="bg-yellow-200 px-1">Anthony Ciponaer</span>
                                        {' '}and{' '}
                                        <span className="bg-yellow-200 px-1">Ashley Harmer</span>
                                        {' '}permission to be my authorized user for 365 days and agent of record on my current plan or switch my plan to a different carrier or plan. <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex space-x-6">
                                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                                            <input
                                                type="radio"
                                                name="permissionGiven"
                                                value="Yes"
                                                checked={enrollmentData.permissionGiven === 'Yes'}
                                                onChange={() => updateEnrollmentData('permissionGiven', 'Yes')}
                                                className="form-radio h-5 w-5 text-indigo-600"
                                            />
                                            <span className="ml-2 text-gray-700 font-medium">Yes</span>
                                        </label>
                                    </div>
                                    {errors.permissionGiven && <p className="mt-2 text-sm text-red-600">{errors.permissionGiven}</p>}
                                </div>
                            )}

                            {/* Insurance Enrollment Status */}
                            {isFmoMasterView && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                                        Insurance Enrollment Status <span className="text-red-500">*</span>
                                    </label>
                                    <div className="space-y-2">
                                        <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors border-blue-200 bg-blue-50">
                                            <input
                                                type="radio"
                                                name="hasInsurance"
                                                value="No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance"
                                                checked={enrollmentData.hasInsurance === 'No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance'}
                                                onChange={(e) => updateEnrollmentData('hasInsurance', e.target.value)}
                                                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                                            />
                                            <span className="ml-3 text-sm font-medium text-blue-800">No, I do not have any other insurance such as Medicare, Medicaid, Tricare, or Employer Provided Insurance</span>
                                        </label>
                                    </div>
                                    {errors.hasInsurance && <p className="mt-2 text-sm text-red-600">{errors.hasInsurance}</p>}
                                </div>
                            )}

                            {/* Terms Agreement */}
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <label className="block text-sm font-semibold text-gray-800 mb-3">
                                    I agree to the terms and conditions of this enrollment. <span className="text-red-500">*</span>
                                </label>
                                <div className="flex space-x-6">
                                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                                        <input
                                            type="radio"
                                            name="termsAgreement"
                                            value="YES"
                                            checked={enrollmentData.termsAgreement === 'YES'}
                                            onChange={() => updateEnrollmentData('termsAgreement', 'YES')}
                                            className="form-radio h-5 w-5 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700 font-medium">YES</span>
                                    </label>
                                </div>
                                {errors.termsAgreement && <p className="mt-2 text-sm text-red-600">{errors.termsAgreement}</p>}
                            </div>
                        </div>
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400 space-y-1">
                    <div>Application Date: <span className="text-gray-600 font-semibold">{enrollmentData.applicationDate ? new Date(enrollmentData.applicationDate).toLocaleDateString('en-US') : new Date().toLocaleDateString('en-US')}</span></div>
                    <div>IP Address: <span className="text-gray-600 font-semibold">{enrollmentData.ipAddress || enrollmentData.clientIp || 'Awaiting Capture...'}</span></div>
                </div>
            </div>
        </StageWrapper>
    )
}
